import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { loadStripe, Stripe, StripeElements, StripeElementsOptions } from '@stripe/stripe-js';
import { PaymentState } from 'libs/shared/utils/interfaces/payment-state.interface';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { EnvironmentService } from '@offconon/shared/utils/services/environment';

import * as StripeCardPayActions from '../+state/stripe-card-pay.actions';
import { confirmCardPaymentSuccess } from '../+state/stripe-card-pay.actions';
import { StripeCardPayModule } from '../stripe-card-pay.module';

@Component({
  selector: 'offconon-stripe-card-pay',
  template: `
    <p-dialog
      header="{{ 'Payment by bank card' | translate }}"
      [modal]="true"
      [closable]="true"
      [style]="{ width: '50rem' }"
      [breakpoints]="{ '1199px': '75vw', '575px': '90vw' }"
      [(visible)]="payDialogVisible"
      (onShow)="onDialogShow()"
      (onHide)="onDialogHide()">
      <div class="pt-4">
        <div id="card-element"></div>
        @if (cardError) {
          <div class="text-red-500 text-sm mt-2">{{ cardError }}</div>
        }
        @if (!environmentService.environment.production) {
          <div class="mt-4 p-3  rounded text-sm">
            <p class="font-semibold mb-2">Test Card Details:</p>
            <ul class="list-disc pl-4">
              <li>Card number: 4242424242424242</li>
              <li>Expiry: 1234</li>
              <li>CVC: 567</li>
            </ul>
          </div>
        }
      </div>
      <div class="border-t border-gray">
        <div class="pt-3 flex justify-center flex-wrap">
          <button
            pButton
            class="px-5 py-3 p-button-outlined p-button-rounded font-bold w-auto white-space-nowrap"
            style="white-space: nowrap"
            (click)="createToken()">
            {{ paymentStateData.amount | currency: paymentStateData.currency + ' ' }}
            <span class="italic ml-2 text-teal-500">{{ 'Pay' | translate }}</span>
          </button>
        </div>
      </div>
    </p-dialog>
  `,
  styles: [],
  imports: [ButtonModule, CurrencyPipe, DialogModule, TranslateModule, StripeCardPayModule],
})
export class StripeCardPayComponent {
  @Input({ required: true }) paymentStateData: PaymentState;
  @Input({ required: true }) paymentType: 'wallet' | 'subscription' | 'shoppingCart';

  @Output() paymentEvent = new EventEmitter<any>();
  @Output() paymentStartEvent = new EventEmitter<void>();

  environmentService = inject(EnvironmentService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  payDialogVisible = false;
  stripe: Stripe | null = null;
  elements: StripeElements | null = null;
  card: any;
  cardError: string | null = null;
  private actions$ = inject(Actions);
  constructor() {
    this.actions$
      .pipe(ofType(confirmCardPaymentSuccess))
      .pipe(takeUntilDestroyed())
      .subscribe((action) => {
        this.paymentEvent.emit(action.result);
      });
  }

  onDialogShow() {
    if (!this.stripe && this.paymentStateData?.publicKey) {
      loadStripe(this.paymentStateData.publicKey).then((stripe) => {
        this.stripe = stripe;
        if (this.stripe && !this.card) {
          const options: StripeElementsOptions = {
            locale: this.translate.currentLang as any,
            currency: this.paymentStateData?.currency?.toLowerCase(),

            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#0570de',
                colorBackground: '#ffffff',
                colorText: '#30313d',
                colorDanger: '#df1b41',
                fontFamily: 'Ideal Sans, system-ui, sans-serif',
                spacingUnit: '4px',
                borderRadius: '4px',
              },
            },
          };

          this.elements = this.stripe.elements(options);
          this.card = this.elements.create('card', {
            hidePostalCode: true,
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          });

          // Small timeout to ensure the dialog is fully rendered
          setTimeout(() => {
            this.card.mount('#card-element');
            this.card.on('change', (event: any) => {
              this.cardError = event.error ? event.error.message : '';
            });
          }, 100);
        }
      });
    }
  }

  onDialogHide() {
    if (this.card) {
      this.card.unmount();
      this.card = null;
    }
  }

  async createToken(): Promise<void> {
    if (!this.stripe || !this.card) {
      return;
    }
    this.paymentStartEvent.emit();
    this.payDialogVisible = false;
    const { token, error } = await this.stripe.createToken(this.card);

    if (error) {
      this.cardError = error.message || 'An error occurred';
      return;
    }
    if (token) {
      switch (this.paymentType) {
        case 'wallet':
          this.store.dispatch(
            StripeCardPayActions.walletPayment({
              token,
              addressBookId: this.paymentStateData.addressBook.id!,
              amount: this.paymentStateData.amount,
              currency: this.paymentStateData.currency,
            }),
          );
          break;
        case 'subscription':
          this.store.dispatch(
            StripeCardPayActions.subscriptionPayment({
              token,
              addressBookId: this.paymentStateData.addressBook.id!,
            }),
          );
          break;
        default:
          break;
      }
    }
  }

  triggerPayment(): void {
    this.payDialogVisible = true;
  }
}
